import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

type LayoutContextProps = {
  toggleTabBarVisibility: () => void;
  toggleHeaderMenuVisability: () => void;
  setTabsState: React.Dispatch<React.SetStateAction<boolean>>;
  setBackLink: React.Dispatch<React.SetStateAction<string | undefined>>;
  setMessage: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSubTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDoShowDots: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setMapOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowHeader: React.Dispatch<React.SetStateAction<boolean>>;
  setBelowHeaderContent: React.Dispatch<React.SetStateAction<ReactNode>>;
  isMapOpen: boolean;
  isMenuOpen: boolean;
  currentTitle: string;
};

const layoutContextDefualtValue: LayoutContextProps = {
  toggleTabBarVisibility: () => {},
  toggleHeaderMenuVisability: () => {},
  setTabsState: () => {},
  setBackLink: () => {},
  setMessage: () => {},
  setSubTitle: () => {},
  setDoShowDots: () => {},
  setMapOpen: () => {},
  setShowHeader: () => {},
  setBelowHeaderContent: () => {},
  isMenuOpen: false,
  isMapOpen: false,
  currentTitle: '',
};

const LayoutContext = createContext<LayoutContextProps>(layoutContextDefualtValue);

export const useLayoutContext = () => useContext(LayoutContext);

type LayoutContextProviderProps = {
  setTabsState: React.Dispatch<React.SetStateAction<boolean>>;
  setBackLink: React.Dispatch<React.SetStateAction<string | undefined>>;
  setMessage: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSubTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDoShowDots: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setHeaderMenu: React.Dispatch<React.SetStateAction<boolean>>;
  setMapOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowHeader: React.Dispatch<React.SetStateAction<boolean>>;
  setBelowHeaderContent: React.Dispatch<React.SetStateAction<ReactNode>>;
  isMapOpen: boolean;
  isMenuOpen: boolean;
  children: React.ReactNode;
  currentTitle: string;
};
export const LayoutContextProvider = (props: LayoutContextProviderProps) => {
  const {
    setTabsState,
    children,
    setBackLink,
    setMessage,
    setSubTitle,
    setDoShowDots,
    setHeaderMenu,
    setMapOpen,
    setShowHeader,
    isMapOpen,
    isMenuOpen,
    setBelowHeaderContent,
    currentTitle,
  } = props;

  const toggleTabBarVisibility = () => {
    setTabsState(prev => !prev);
  };
  const toggleHeaderMenuVisability = () => {
    setHeaderMenu(prev => !prev);
  };

  const value = useMemo(
    () => ({
      toggleTabBarVisibility,
      setTabsState,
      setBackLink,
      setMessage,
      setSubTitle,
      setDoShowDots,
      toggleHeaderMenuVisability,
      setMapOpen,
      setShowHeader,
      isMapOpen,
      isMenuOpen,
      setBelowHeaderContent,
      currentTitle,
    }),
    [isMenuOpen, isMapOpen, currentTitle]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};
